import React, { useEffect, useMemo, useState } from 'react';
import './reviews-table.css';
import KPAReviewSummaryModal from './KPAReviewSummaryModal';

/**
 * Props:
 *  - items: Array<{ kpa: {id,name,weight,description?,alignment?,timeframe?}, review: {...} }>
 *  - role: "employee" | "manager"  (controls which comment field is editable)
 *  - onSaveComment: async ({ id, value }) => void
 *  - contractId?: number                                   // optional, only if you want modal to enrich details
 *  - loadKpaDetails?: (kpaId: number) => Promise<object?> // optional, used by modal to fetch missing KPA fields
 */
export default function KPAReviewSummaryTable({
                                                  items = [],
                                                  role = 'employee',
                                                  onSaveComment,
                                                  contractId,
                                                  loadKpaDetails,
                                              }) {
    // Local cache so table reflects changes immediately (optimistic)
    const [local, setLocal] = useState({});
    useEffect(() => {
        const seed = Object.fromEntries(
            (items || []).map(({ review }) => [review?.id, review])
        );
        setLocal(seed);
    }, [items]);

    // Modal state
    const [modalOpen, setModalOpen] = useState(false);
    const [modalKpa, setModalKpa] = useState(null);
    const [modalReview, setModalReview] = useState(null);

    // Group by KPA id, keep strict numeric order by KPA id
    const sections = useMemo(() => {
        const byId = new Map();
        for (const row of items || []) {
            const kpa = row.kpa || {};
            const kpaId = kpa.id ?? `null-${row.review?.id}`;
            if (!byId.has(kpaId)) {
                byId.set(kpaId, { kpa, rows: [] });
            }
            byId.get(kpaId).rows.push(row);
        }

        // Ensure exactly two logical rows per KPA in the table view:
        //  - "Manager" row perspective
        //  - "Employee" row perspective
        // If your array already contains just one object per KPA, we’ll synthesize the second row for display purposes.
        const result = Array.from(byId.values());
        result.sort((a, b) => (Number(a.kpa?.id) || 0) - (Number(b.kpa?.id) || 0));
        return result.map(({ kpa, rows }) => {
            // Find a single combined review object for convenience
            // (We expect aggregator to produce one review row per KPA.)
            const base = rows[0] || {};
            return { kpa, review: base.review || {} };
        });
    }, [items]);

    const openForRole = (kpaObj, reviewObj) => {
        setModalKpa(kpaObj);
        // prefer the freshest review from local cache
        const cached = reviewObj?.id ? local[reviewObj.id] || reviewObj : reviewObj;
        setModalReview(cached);
        setModalOpen(true);
    };

    const handleSaveFromModal = async ({ id, value }) => {
        // Update local cache optimistically
        setLocal(prev => {
            const current = prev[id] || {};
            const patch =
                role === 'employee'
                    ? { employee_comment: value }
                    : { manager_comment: value };
            return { ...prev, [id]: { ...current, ...patch } };
        });

        await onSaveComment({ id, value });
        setModalOpen(false);
    };

    // Helpers to safely read from local cache
    const getReview = (review) => {
        if (!review?.id) return review || {};
        return local[review.id] || review;
    };

    return (
        <div className="card mt-5">
            <div className="card-header">
                <h6 className="mb-0">KPA Summary</h6>
            </div>
            <div className="card-body">
                <div className="reviews-table mt-4">
                    <table>
                        <thead>
                        <tr className="rt-head-top">
                            <th className="rt-counter">#</th>
                            <th className="rt-name">KPA Summary and Comments</th>
                            <th className="rt-role" style={{ width: 120 }}>Role</th>
                            <th className="rt-num" style={{ width: 90 }}>Rating</th>
                            <th className="rt-num" style={{ width: 120 }}>Description</th>
                            <th className="rt-comment-cell">Comment</th>
                            <th className="rt-actions" style={{ width: 120 }}>Action</th>
                        </tr>
                        </thead>

                        <tbody>
                        {sections.length === 0 && (
                            <tr>
                                <td colSpan={7} className="text-center text-muted py-3">
                                    No KPA summaries.
                                </td>
                            </tr>
                        )}

                        {sections.map((sec, i) => {
                            const counter = `KPA ${String(i + 1).padStart(2, '0')}`;
                            const review = getReview(sec.review);
                            // Render two perspective rows: Manager + Employee
                            const managerRow = {
                                label: 'Manager',
                                rating: review?.manager_rating_id ?? '—',
                                desc: review?.manager_description ?? '—',
                                comment: review?.manager_comment ?? '',
                                canAct: role === 'manager' && !!review?.id,
                                onOpen: () => openForRole(sec.kpa, review),
                            };
                            const employeeRow = {
                                label: 'Employee',
                                rating: review?.employee_rating_id ?? '—',
                                desc: review?.employee_description ?? '—',
                                comment: review?.employee_comment ?? '',
                                canAct: role === 'employee' && !!review?.id,
                                onOpen: () => openForRole(sec.kpa, review),
                            };

                            return (
                                <React.Fragment key={String(sec.kpa?.id ?? i)}>
                                    {/* Manager row */}
                                    <tr className={`kpa-row ${i % 2 === 0 ? 'alt-row' : ''}`}>
                                        <td className="rt-counter" rowSpan={2}>
                                            {counter}
                                        </td>
                                        <td className="rt-name" rowSpan={2}>
                                            {sec.kpa?.name ?? '—'}
                                        </td>
                                        <td className="rt-num">{managerRow.label}</td>
                                        <td className="rt-num">{managerRow.rating}</td>
                                        <td className="rt-num">{managerRow.desc}</td>
                                        <td className="rt-comment-cell">
                                            {managerRow.comment || ''}
                                        </td>
                                        <td className="rt-actions">
                                            {managerRow.canAct ? (
                                                <button className="rt-btn rt-btn-primary" onClick={managerRow.onOpen}>
                                                    Open
                                                </button>
                                            ) : null}
                                        </td>
                                    </tr>

                                    {/* Employee row */}
                                    <tr className={`kpa-row ${i % 2 === 0 ? 'alt-row' : ''}`}>
                                        <td className="rt-num">{employeeRow.label}</td>
                                        <td className="rt-num">{employeeRow.rating}</td>
                                        <td className="rt-num">{employeeRow.desc}</td>
                                        <td className="rt-comment-cell">
                                            {employeeRow.comment || ''}
                                        </td>
                                        <td className="rt-actions">
                                            {employeeRow.canAct ? (
                                                <button className="rt-btn rt-btn-primary" onClick={employeeRow.onOpen}>
                                                    Open
                                                </button>
                                            ) : null}
                                        </td>
                                    </tr>
                                </React.Fragment>
                            );
                        })}
                        </tbody>
                    </table>

                    {/* Details Modal */}
                    <KPAReviewSummaryModal
                        show={modalOpen}
                        onClose={() => setModalOpen(false)}
                        role={role}
                        kpa={modalKpa}
                        review={modalReview}
                        onSave={handleSaveFromModal}
                        loadKpaDetails={loadKpaDetails}
                        contractId={contractId}
                    />
                </div>
            </div>
        </div>

    );
}